// Tab "Relatórios" (§6.2) — wave 2 EDITÁVEL: 2 switches broker com
// PUT upsert imediato no toggle (o payload sempre leva as DUAS
// flags). GET 404 = não configurado → callout + switches em off; o
// primeiro toggle CRIA a configuração e o callout some após o
// refetch da invalidação.
import { InfoIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import {
  useAdminWhiteLabelReportConfiguration,
  useUpsertAdminWhiteLabelReportConfigurationMutation,
} from "@/services/admin"

import { FieldInfo } from "./field-info"

const panelPrefix = "pages.adminWhiteLabels"

// As 2 flags do contrato, na ordem do wireframe.
const REPORT_FLAGS = [
  "defaulterBrokerReport",
  "unpaidCustomerBrokerReport",
] as const
type ReportFlag = (typeof REPORT_FLAGS)[number]

type ReportsTabProps = {
  uuid: string
}

export const ReportsTab = ({ uuid }: ReportsTabProps) => {
  const { t } = useTranslation()
  const { data, isLoading, isError, refetch } =
    useAdminWhiteLabelReportConfiguration(uuid)
  const upsertMutation =
    useUpsertAdminWhiteLabelReportConfigurationMutation(
      uuid
    )

  if (isLoading) {
    return (
      <div
        data-testid="wl-reports-skeleton"
        className="space-y-3"
      >
        {Array.from({ length: 2 }).map((_, index) => (
          <Skeleton
            key={`wl-reports-skeleton-${index}`}
            className="h-14 w-full rounded-xl"
          />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-6">
        <p className="text-center text-sm text-red-700">
          {t(`${panelPrefix}.reports.loadError`)}
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => refetch()}
        >
          {t(`${panelPrefix}.retry`)}
        </Button>
      </div>
    )
  }

  // 404 → null: não configurado ainda (ambos off; PUT cria).
  const current = {
    defaulterBrokerReport:
      data?.defaulterBrokerReport ?? false,
    unpaidCustomerBrokerReport:
      data?.unpaidCustomerBrokerReport ?? false,
  }

  // Toggle = upsert com as DUAS flags (a alterada + a atual).
  const handleToggle = (
    flag: ReportFlag,
    nextValue: boolean
  ) => {
    upsertMutation.mutate({
      ...current,
      [flag]: nextValue,
    })
  }

  return (
    <div className="space-y-3">
      {data === null && (
        <div
          data-testid="wl-reports-not-configured"
          className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4"
        >
          <InfoIcon
            className="mt-0.5 size-4 shrink-0 text-blue-600"
            aria-hidden="true"
          />
          <p className="text-sm text-blue-800">
            {t(`${panelPrefix}.reports.notConfigured`)}
          </p>
        </div>
      )}

      <ul className="flex flex-col gap-3">
        {REPORT_FLAGS.map((flag) => (
          <li
            key={flag}
            data-testid={`wl-report-${flag}`}
            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card p-4 shadow-sm"
          >
            <span className="flex items-center gap-1.5 text-sm font-medium">
              {t(`${panelPrefix}.reports.flags.${flag}`)}
              <FieldInfo
                hintKey={`${panelPrefix}.reports.descriptions.${flag}`}
              />
            </span>
            <Switch
              checked={current[flag]}
              disabled={upsertMutation.isPending}
              aria-label={t(
                `${panelPrefix}.reports.flags.${flag}`
              )}
              onCheckedChange={(nextValue) =>
                handleToggle(flag, nextValue)
              }
            />
          </li>
        ))}
      </ul>
    </div>
  )
}
